import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../lib/api.js';
import { setKey, setUser } from '../lib/auth.jsx';
import { msalEnabled, loginWithMicrosoft } from '../lib/msal.js';
import { googleEnabled, googleClientId, signInWithGoogle } from '../lib/google.js';

export default function Login() {
  const [tab, setTab] = useState('signin');     // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [signupTier, setSignupTier] = useState('payg');
  const [legacyEmail, setLegacyEmail] = useState('');
  const [legacyKey, setLegacyKey] = useState('');
  const [showLegacy, setShowLegacy] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const SIGNUP_TIERS = [
    { key: 'payg',         label: 'Single Map',   price: 29,   priceUnit: 'one-time',  tagline: 'No subscription. $29/map.' },
    { key: 'solo',         label: 'Solo',         price: 49,   priceUnit: '/mo',       tagline: '50 credits / mo' },
    { key: 'starter',      label: 'Starter',      price: 149,  priceUnit: '/mo',       tagline: '150 credits / mo' },
    { key: 'professional', label: 'Professional', price: 349,  priceUnit: '/mo',       tagline: '400 credits / mo' },
    { key: 'business',     label: 'Business',     price: 799,  priceUnit: '/mo',       tagline: '1,000 credits / mo' },
    { key: 'enterprise',   label: 'Enterprise',   price: 2500, priceUnit: '/mo',       tagline: '2,500+ credits / mo' },
  ];
  const [forgotShown, setForgotShown] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const nav = useNavigate();
  const loc = useLocation();

  const after = (data) => {
    if (!data?.user) throw new Error('Authentication failed');
    if (data.access_key) setKey(data.access_key);
    setUser(data.user);
    const dest = (loc.state && loc.state.from) ? loc.state.from
              : (data.user.role === 'admin' ? '/admin' : '/dashboard');
    nav(dest, { replace: true });
  };

  const submitSignin = async (e) => {
    e.preventDefault(); setErr(''); setBusy('signin');
    try {
      const data = await api.emailLogin({ email: email.trim(), password });
      after(data);
    } catch (e) {
      // 402 means the user signed up but never completed Stripe payment.
      // Mint a fresh checkout URL and redirect them straight back to Stripe.
      if (e?.status === 402 && e?.data?.checkout_url) {
        window.location.href = e.data.checkout_url;
        return;
      }
      if (e?.status === 402) {
        setErr('Your account is not active yet — payment was never completed. Contact info@atlasgeo.ai to finish setup.');
      } else {
        setErr(e?.data?.detail || e?.data?.error || e.message || 'Sign-in failed');
      }
    } finally { setBusy(''); }
  };

  const submitSignup = async (e) => {
    e.preventDefault(); setErr(''); setBusy('signup');
    if (!agreedToTerms) { setErr('Please accept the Terms to create an account.'); setBusy(''); return; }
    try {
      const data = await api.emailSignup({
        email: email.trim(),
        name: name.trim(),
        password,
        tier: signupTier,
        accept_terms: true,
      });
      // All tiers are paid — backend always returns a Stripe Checkout URL.
      // Redirect the browser there to complete payment. Once Stripe fires
      // the webhook the account flips to status:'active' and the user can
      // sign in.
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      // Defensive fallback: if Stripe is not configured (dev), legacy code
      // path returns user+access_key directly.
      after(data);
    } catch (e) {
      const code = e?.data?.error;
      const msg = code === 'email_taken' ? 'An account with that email already exists. Try signing in.'
                : code === 'password_too_short' ? 'Password must be at least 8 characters.'
                : code === 'password_too_common' ? 'Pick a less common password.'
                : code === 'invalid_email' ? 'That email looks invalid.'
                : code === 'invalid_tier' ? 'Pick a plan before continuing.'
                : code === 'billing_not_configured' ? 'Billing is not yet enabled. Email info@atlasgeo.ai.'
                : e?.data?.detail || e.message || 'Signup failed';
      setErr(msg);
    } finally { setBusy(''); }
  };

  const submitLegacy = async (e) => {
    e.preventDefault(); setErr(''); setBusy('legacy');
    setKey(legacyKey.trim());
    try {
      const data = await api.login(legacyEmail.trim(), legacyKey);
      after(data);
    } catch (e) {
      setErr(e?.data?.detail || e.message || 'Invalid email or access key');
    } finally { setBusy(''); }
  };

  const onMicrosoft = async () => {
    setErr(''); setBusy('msal');
    try { await loginWithMicrosoft(); }
    catch (e) { setErr(e.message || 'Microsoft sign-in unavailable'); setBusy(''); }
  };

  const onGoogle = async () => {
    setErr(''); setBusy('google');
    try {
      const { id_token } = await signInWithGoogle();
      if (!id_token) throw new Error('Google sign-in cancelled');
      try {
        const data = await api.googleSignin({ id_token, accept_terms: tab === 'signup' ? true : agreedToTerms });
        after(data);
      } catch (e) {
        // signup_required → if user didn't tick terms, prompt now
        if (e?.data?.signup_required) {
          if (!agreedToTerms) {
            setErr('No MAPTRIUM account for that Google email. Tick the Terms box and try again to create one.');
          } else {
            const data2 = await api.googleSignin({ id_token, accept_terms: true });
            after(data2);
          }
        } else {
          setErr(e?.data?.detail || e.message || 'Google sign-in failed');
        }
      }
    } catch (e) {
      setErr(e.message || 'Google sign-in failed');
    } finally { setBusy(''); }
  };

  const onForgot = async (e) => {
    e.preventDefault(); setErr(''); setForgotMsg('');
    try {
      await api.requestPasswordReset({ email: forgotEmail.trim() });
      setForgotMsg('If that email is registered, a reset link is on its way. Check your inbox.');
    } catch (e) {
      setForgotMsg('Could not send right now — try again in a minute.');
    }
  };

  const tabsAvailable = (
    <div className="flex bg-slate-100 rounded-md p-1 mb-5 text-sm">
      {['signin','signup'].map(t => (
        <button
          key={t}
          onClick={() => { setTab(t); setErr(''); }}
          className={`flex-1 py-2 rounded ${tab===t ? 'bg-white shadow text-navy font-semibold' : 'text-ink-muted'}`}
        >{t === 'signin' ? 'Sign in' : 'Create account'}</button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy via-navy-700 to-navy-800 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-6">
          <svg width="40" height="40" viewBox="0 0 64 64" aria-hidden="true">
            <rect width="64" height="64" rx="10" fill="#DBC078"/>
            <path d="M14 46 L32 14 L50 46 Z" fill="none" stroke="#032953" strokeWidth="4" strokeLinejoin="round"/>
            <circle cx="32" cy="32" r="4" fill="#032953"/>
          </svg>
          <div className="flex flex-col text-white">
            <span className="font-bold text-xl tracking-tight">MAPTRIUM™</span>
            <span className="text-[10px] text-white/60 uppercase tracking-widest">by Atlas Geo</span>
          </div>
        </Link>

        <div className="bg-white rounded-xl shadow-2xl p-6">
          {tabsAvailable}

          {tab === 'signup' && (
            <div className="mb-4 bg-amber-50 border border-amber-300 rounded p-3 text-xs text-amber-900">
              <strong>By creating an account, you agree:</strong>
              <ul className="mt-1 ml-4 list-disc space-y-1">
                <li>Billing is in advance and <strong>non-refundable</strong>.</li>
                <li>You are responsible for the accuracy of any data you upload. Final QA is the <strong>client's sole responsibility</strong>.</li>
                <li>Atlas Geo Technologies LLC is not liable for decisions made from inaccurate input data.</li>
              </ul>
              <div className="mt-2">
                <Link to="/terms" target="_blank" className="font-semibold underline">Read the full Terms ↗</Link>
              </div>
              <label className="flex items-start gap-2 mt-2">
                <input type="checkbox" checked={agreedToTerms} onChange={e=>setAgreedToTerms(e.target.checked)} className="mt-0.5" />
                <span>I have read and agree to the Terms.</span>
              </label>
            </div>
          )}

          <div className="space-y-2 mb-4">
            {googleEnabled() && googleClientId() && (
              <button
                onClick={onGoogle}
                disabled={busy === 'google' || (tab === 'signup' && !agreedToTerms)}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border-2 border-slate-200 hover:border-navy bg-white text-ink font-semibold rounded-md transition disabled:opacity-50"
              >
                <GoogleLogo />
                <span>{busy === 'google' ? 'Opening Google…' : (tab === 'signup' ? 'Sign up with Google' : 'Sign in with Google')}</span>
              </button>
            )}
            {msalEnabled() && (
              <button
                onClick={onMicrosoft}
                disabled={busy === 'msal' || (tab === 'signup' && !agreedToTerms)}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border-2 border-slate-200 hover:border-navy bg-white text-ink font-semibold rounded-md transition disabled:opacity-50"
              >
                <MicrosoftLogo />
                <span>{busy === 'msal' ? 'Redirecting…' : (tab === 'signup' ? 'Sign up with Microsoft' : 'Sign in with Microsoft')}</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-3 my-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-ink-muted uppercase tracking-widest">or email</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {tab === 'signin' ? (
            <form onSubmit={submitSignin} className="space-y-3">
              <input type="email" required autoComplete="username" placeholder="you@example.com"
                className="input" value={email} onChange={e => setEmail(e.target.value)} />
              <input type="password" required autoComplete="current-password" placeholder="Password"
                className="input" value={password} onChange={e => setPassword(e.target.value)} />
              {err && <div className="text-sm text-red-600">{err}</div>}
              <button className="btn btn-primary w-full" disabled={busy === 'signin' || !email || !password}>
                {busy === 'signin' ? 'Signing in…' : 'Sign in'}
              </button>
              <button type="button" onClick={() => { setForgotShown(true); setForgotEmail(email); }} className="text-xs text-navy underline w-full text-center">
                Forgot your password?
              </button>
            </form>
          ) : (
            <form onSubmit={submitSignup} className="space-y-3">
              <input type="text" placeholder="Full name" className="input" value={name} onChange={e => setName(e.target.value)} />
              <input type="email" required autoComplete="email" placeholder="you@example.com"
                className="input" value={email} onChange={e => setEmail(e.target.value)} />
              <input type="password" required autoComplete="new-password" placeholder="Pick a password (8+ chars)"
                className="input" value={password} onChange={e => setPassword(e.target.value)} />

              {/* Tier picker — all tiers are paid; user is redirected to
                  Stripe Checkout immediately after Create account. PAYG is
                  one-time, all others are subscriptions. */}
              <div>
                <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-2">Pick your plan</div>

                {/* PAYG gets its own highlighted row — it's the no-commitment entry point */}
                <label
                  className={`flex items-center gap-3 p-3 rounded-md border-2 cursor-pointer transition mb-2 ${
                    signupTier === 'payg' ? 'border-gold bg-gold/10' : 'border-slate-200 hover:border-gold/60'
                  }`}
                >
                  <input
                    type="radio"
                    className="sr-only"
                    name="signup-tier"
                    value="payg"
                    checked={signupTier === 'payg'}
                    onChange={() => setSignupTier('payg')}
                  />
                  <div className="flex-shrink-0 text-2xl">🗺️</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-semibold text-navy text-sm">Single Map</span>
                      <span className="text-navy font-bold text-sm">$29<span className="text-[10px] font-normal text-ink-muted ml-0.5">/map</span></span>
                    </div>
                    <div className="text-[10px] text-ink-muted">No subscription. $29 each time you generate a map.</div>
                  </div>
                </label>

                {/* Subscription tiers in a 2x2 grid below */}
                <div className="grid grid-cols-2 gap-2">
                  {SIGNUP_TIERS.filter(t => t.key !== 'payg').map(t => (
                    <label key={t.key}
                      className={`flex flex-col p-2 rounded-md border-2 cursor-pointer text-xs transition ${
                        signupTier === t.key ? 'border-navy bg-navy/5' : 'border-slate-200 hover:border-navy/50'
                      }`}
                    >
                      <input
                        type="radio"
                        className="sr-only"
                        name="signup-tier"
                        value={t.key}
                        checked={signupTier === t.key}
                        onChange={() => setSignupTier(t.key)}
                      />
                      <span className="font-semibold text-navy">{t.label}</span>
                      <span className="text-navy font-bold">${t.price}{t.priceUnit}</span>
                      <span className="text-[10px] text-ink-muted">{t.tagline}</span>
                    </label>
                  ))}
                </div>
                <div className="text-[10px] text-ink-muted mt-1">
                  Next step is Stripe Checkout. Your account activates as soon as payment clears.
                </div>
              </div>

              {err && <div className="text-sm text-red-600">{err}</div>}
              <button className="btn btn-primary w-full" disabled={busy === 'signup' || !email || !password || !agreedToTerms}>
                {busy === 'signup' ? 'Creating…' : 'Continue to payment →'}
              </button>
            </form>
          )}

          <div className="mt-5 pt-4 border-t border-slate-200 text-xs text-ink-muted text-center">
            <button onClick={() => setShowLegacy(s => !s)} className="text-ink-muted underline">
              {showLegacy ? 'Hide' : 'Use'} legacy email + access-key form
            </button>
          </div>

          {showLegacy && (
            <form onSubmit={submitLegacy} className="space-y-3 mt-3 p-3 bg-slate-50 rounded">
              <div className="text-xs text-ink-muted">For admins/partners using the original sign-in:</div>
              <input type="email" autoComplete="off" placeholder="Email" className="input" value={legacyEmail} onChange={e => setLegacyEmail(e.target.value)} />
              <input type="password" autoComplete="off" placeholder="Access key" className="input" value={legacyKey} onChange={e => setLegacyKey(e.target.value)} />
              <button className="btn btn-ghost w-full text-sm" disabled={busy === 'legacy'}>
                {busy === 'legacy' ? 'Checking…' : 'Sign in with access key'}
              </button>
            </form>
          )}
        </div>

        {forgotShown && (
          <div className="mt-4 bg-white rounded-xl shadow p-4">
            <div className="font-semibold text-navy mb-2">Reset your password</div>
            <form onSubmit={onForgot} className="space-y-2">
              <input type="email" required placeholder="Email" className="input" value={forgotEmail} onChange={e=>setForgotEmail(e.target.value)} />
              <button className="btn btn-primary w-full">Send reset link</button>
              {forgotMsg && <div className="text-sm text-emerald-700">{forgotMsg}</div>}
              <button type="button" onClick={() => setForgotShown(false)} className="text-xs text-ink-muted underline w-full text-center">cancel</button>
            </form>
          </div>
        )}

        <p className="text-center text-xs text-white/50 mt-6">
          © {new Date().getFullYear()} Atlas Geo Technologies LLC · <Link to="/terms" className="underline">Terms</Link>
        </p>
      </div>
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 23 23" aria-hidden="true">
      <rect width="10" height="10" x="1" y="1" fill="#F25022" />
      <rect width="10" height="10" x="12" y="1" fill="#7FBA00" />
      <rect width="10" height="10" x="1" y="12" fill="#00A4EF" />
      <rect width="10" height="10" x="12" y="12" fill="#FFB900" />
    </svg>
  );
}

function GoogleLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 18 18" aria-hidden="true">
      <path d="M17.64 9.2c0-.64-.05-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.08-1.79 2.72v2.26h2.9c1.7-1.57 2.69-3.88 2.69-6.62z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.9-2.26c-.81.55-1.85.87-3.06.87-2.35 0-4.34-1.58-5.05-3.71H.96v2.33A9 9 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.95 10.72A5.4 5.4 0 0 1 3.66 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.46.35 2.84.96 4.05l2.99-2.33z" fill="#FBBC04"/>
      <path d="M9 3.58c1.32 0 2.5.45 3.44 1.34l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.95l2.99 2.33C4.66 5.15 6.65 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}
